import { useState, FormEvent } from 'react';

export default function ConsultingSimple() {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate required fields
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const business = formData.get('business') as string;
    const challenge = formData.get('challenge') as string;

    if (!name || !email || !business || !challenge) {
      setSubmitError('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    // Validate textarea length
    if (challenge.length > 500) {
      setSubmitError('Challenge description must be 500 characters or less');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5v94dj0o41p73073r6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        form.reset();
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Apply for a Consulting Session
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to transform your business? Fill out the application below and let's explore how we can work together.
          </p>
          
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-8 bg-gray-900 text-white px-10 py-4 rounded-full font-medium hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Start Application
            </button>
          )}
        </div>

        {showForm && (
          <form 
            id="consulting-application"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-gray-50 rounded-2xl p-8 md:p-12 shadow-sm animate-fadeIn"
          >
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="business" className="block text-sm font-medium text-gray-900 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  id="business"
                  name="business"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Your business name"
                />
              </div>

              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-900 mb-2">
                  Website (Optional)
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div>
                <label htmlFor="challenge" className="block text-sm font-medium text-gray-900 mb-2">
                  What's your biggest business challenge right now? * (Max 500 characters)
                </label>
                <textarea
                  id="challenge"
                  name="challenge"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Describe your current challenge..."
                />
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              <div>
                <label htmlFor="revenue" className="block text-sm font-medium text-gray-900 mb-2">
                  Annual Revenue Range (Optional)
                </label>
                <select
                  id="revenue"
                  name="revenue"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select range</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                  <option value="under-100k">Under $100K</option>
                  <option value="100k-500k">$100K - $500K</option>
                  <option value="500k-1m">$500K - $1M</option>
                  <option value="1m-5m">$1M - $5M</option>
                  <option value="over-5m">Over $5M</option>
                </select>
              </div>

              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              {submitSuccess && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-600">
                    Thank you! Your application has been submitted successfully. We'll be in touch soon.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gray-900 text-white px-8 py-4 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
